import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid2 as Grid,
  Stack,
  Typography,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import ScrollToPlugin from "gsap/ScrollToPlugin";
import img1 from "../images/analytics-screen.png";
import img2 from "../images/mobile-22.png";
import img3 from "../images/trinity-staff.png";
import guest from "../images/guest.png";
import admin from "../images/admin.png";
import staff from "../images/staff.png";

let gsapReady = false;
if (typeof window !== "undefined") {
  try {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
    gsapReady = true;
  } catch (error) {
    console.warn("GSAP plugins registration failed:", error);
  }
}

const features = [
  {
    icon: admin,
    title: "Management",
    shortDescription:
      "Consolidate your commercial parking operations into one platform",
    fullDescription:
      "Admins have full control monitor staff performance, view tip analytics, and access detailed guest feedback to enhance hospitality services.",
    image: img1,
  },
  {
    icon: staff,
    title: "Staff",
    shortDescription: "Unmatched Customization from Chatbot environment",
    fullDescription:
      "Staff can easily receive digital tips, track their earnings, and get instant feedback boosting motivation and recognition.",
    image: img3,
  },
  {
    icon: guest,
    title: "Guest",
    shortDescription: "Create and Launch new locations instantaneously",
    fullDescription:
      "Guests simply scan a QR code to leave tips instantly, without needing cash or apps making gratitude effortless and modern.",
    image: img2,
  },
];

const Trinity = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const featuresRef = useRef([]);
  const imageRef = useRef(null);
  const imageContainerRef = useRef(null);
  const activeIndexRef = useRef(0);
  const isManualChangeRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleFeatureClick = (index) => {
    if (activeIndex === index) return;

    isManualChangeRef.current = true;
    setActiveIndex(index);
    animateFeatureChange(index);

    if (!isMobile && featuresRef.current[index]) {
      featuresRef.current[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }

    setTimeout(() => {
      isManualChangeRef.current = false;
    }, 300);
  };

  const animateFeatureChange = (newIndex) => {
    if (!imageRef.current || !imageContainerRef.current) return;

    gsap.killTweensOf(imageRef.current);

    gsap.fromTo(
      imageRef.current,
      { opacity: 0.01, scale: 0.98 },
      {
        opacity: 1,
        // scale: 1,
        duration: 1.0,
        ease: "expoScale(0.5,7,none)",
        overwrite: true,
        immediateRender: false,
      }
    );

    features.forEach((_, i) => {
      if (featuresRef.current[i]) {
        gsap.to(featuresRef.current[i], {
          // scale: i === newIndex ? 1.05 : 1,
          opacity: i === newIndex ? 1 : 0.5,
          duration: 1,
          ease: "expoScale(0.5,7,none)",
        });
      }
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      isManualChangeRef.current = false;
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMobile || !gsapReady || !sectionRef.current) return;

    const refreshTriggers = () => {
      ScrollTrigger.refresh();
    };

    const images = sectionRef.current.querySelectorAll("img");
    let loadedCount = 0;
    images.forEach((img) => {
      if (img.complete) {
        loadedCount++;
      } else {
        img.addEventListener("load", () => {
          loadedCount++;
          if (loadedCount === images.length) {
            setTimeout(refreshTriggers, 300);
          }
        });
      }
    });

    if (loadedCount === images.length) {
      setTimeout(refreshTriggers, 300);
    }

    ScrollTrigger.getAll().forEach((t) => t.kill());
    const triggers = [];

    const anim = gsap.context(() => {
      featuresRef.current.forEach((el, i) => {
        if (!el) return;

        const trigger = ScrollTrigger.create({
          trigger: el,
          start: "top-=120 center",
          end: "center center",
          // markers: true,
          // scrub: 1.5,
          onEnter: () => {
            if (isManualChangeRef.current) return;
            if (activeIndexRef.current !== i) {
              setActiveIndex(i);
              animateFeatureChange(i);
            }
          },
          onEnterBack: () => {
            if (isManualChangeRef.current) return;
            if (activeIndexRef.current !== i) {
              setActiveIndex(i);
              animateFeatureChange(i);
            }
          },
        });
        triggers.push(trigger);
      });
    }, sectionRef);

    return () => {
      anim.revert();
      triggers.forEach((t) => t.kill());
    };
  }, [isMobile]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  return (
    <Box
      ref={sectionRef}
      sx={{
        overflow: "hidden",
        position: "relative",
        my: 5,
      }}
    >
      <Typography
        variant="h3"
        color="#034272"
        textAlign="center"
        sx={{
          fontWeight: 400,
          // mt: 4,
          fontSize: {
            xs: "2rem",
            sm: "2.5rem",
            md: "3rem",
          },
        }}
        gutterBottom
      >
        The Tipkit Trinity
      </Typography>

      <Typography
        gutterBottom
        color="text.secondary"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
          textAlign: "center",
          maxWidth: 700,
          px: 3,
        }}
      >
        Three essential forces — management, staff, and guests — working in
        harmony to unlock a culture of gratitude.
      </Typography>

      <Grid
        container
        spacing={3}
        // ref={sectionRef}
        sx={{
          mt: { xs: "50px", md: "0px", lg: "10px", xl: "10px" },
        }}
      >
        {isMobile ? (
          // MOBILE VERSION
          <Grid size={{ xs: 12 }}>
            <Stack spacing={3} px={2}>
              {features.map((item, i) => (
                <Box
                  key={i}
                  onClick={() => handleFeatureClick(i)}
                  sx={{
                    position: "relative",
                    border: "1px solid rgba(92,154,205,0.3)",
                    borderRadius: "12px",
                    p: 3,
                    backgroundColor: "#fff",
                    boxShadow:
                      activeIndex === i
                        ? "0 8px 22px rgba(92,154,205,0.15)"
                        : "0 1px 3px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "50%",
                      bottom: "-20px",
                      width: "2px",
                      height: "20px",
                      borderLeft: "3px dotted rgba(92,154,205,0.4)",
                      display: i === features.length - 1 ? "none" : "block",
                    },
                  }}
                >
                  <Box
                    display="flex"
                    gap={1.5}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box
                      component="img"
                      src={item.icon}
                      width="39px"
                      sx={{ backgroundColor: "#fff", p: "4px" }}
                    />
                    <Typography
                      color="#034272"
                      fontWeight="bold"
                      fontSize="20px"
                    >
                      {item.title}
                    </Typography>
                  </Box>

                  <Collapse in={activeIndex === i} timeout="auto">
                    <Typography
                      variant="body2"
                      my={3}
                      textAlign={{ xs: "center", md: "start" }}
                    >
                      {item.fullDescription}
                    </Typography>
                    <Box display="flex" justifyContent="center">
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.title}
                        sx={{
                          width: "100%",
                          maxWidth: activeIndex === 0 ? 500 : 300,
                          // maxWidth: 300,
                          borderRadius: 2,
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Stack>
          </Grid>
        ) : (
          // DESKTOP VERSION
          <>
            <Grid
              size={{ xs: 12, md: 7 }}
              display="flex"
              alignItems="flex-start"
              justifyContent="center"
              sx={{
                // minHeight: { md: "80vh", xl: "50vh" },
                px: { xs: 2, md: 6 },
                py: 5,
              }}
            >
              <Stack spacing={3} justifyContent="center">
                {features.map((item, i) => (
                  <Box
                    key={i}
                    ref={(el) => (featuresRef.current[i] = el)}
                    onClick={() => handleFeatureClick(i)}
                    sx={{
                      border: "1px solid rgba(92,154,205,0.3)",
                      borderRadius: "12px",
                      p: 3,
                      maxWidth: "600px",
                      transform: `scale(${i === activeIndex ? 1.02 : 1})`,
                      opacity: i === activeIndex ? 1 : 0.5,
                      transition: "all 0.3s ease",
                      backgroundColor: "rgba(255,255,255,0.02)",
                      boxShadow:
                        i === activeIndex
                          ? "0 8px 22px rgba(92,154,205,0.2)"
                          : "none",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(92,154,205,0.15)",
                        opacity: 0.8,
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: "28px",
                        bottom: "-20px",
                        width: "2px",
                        height: "20px",
                        borderLeft: "3px dotted rgba(92,154,205,0.4)",
                        display: i === features.length - 1 ? "none" : "block",
                      },
                    }}
                  >
                    <Box display="flex" gap={2} alignItems="center">
                      <Box
                        component="img"
                        src={item.icon}
                        width="39px"
                        sx={{
                          backgroundColor: "#fff",
                          p: "4px",
                        }}
                      />
                      <Typography
                        fontSize="20px"
                        color="#034272"
                        fontWeight="bold"
                      >
                        {item.title}
                      </Typography>
                    </Box>

                    <Collapse in={activeIndex === i} timeout="auto">
                      <Typography variant="body2" mt={2}>
                        {item.fullDescription}
                      </Typography>
                    </Collapse>
                  </Box>
                ))}
              </Stack>
            </Grid>

            <Grid
              size={{ xs: 12, md: 5 }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                position: "sticky",
                // minHeight: { md: "80vh", xl: "50vh" },
                zIndex: 10,
              }}
            >
              <Box
                ref={imageContainerRef}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Box
                  ref={imageRef}
                  sx={{
                    width: "100%",
                    maxWidth: activeIndex === 0 ? "500px" : "400px",
                    // maxWidth: "400px",
                    margin: "auto",
                  }}
                >
                  <Box
                    component="img"
                    src={features[activeIndex]?.image || features[0].image}
                    alt={features[activeIndex]?.title || features[0].title}
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxHeight: { xs: "400px", lg: "450px" },
                      objectFit: "contain",
                      borderRadius: 2,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default Trinity;
